// Offline mirror of the gateway's geo reference data (services/geo.ts). Kept in
// sync by hand — the mock is the source of truth for the api shape, the gateway
// is the production path; both return identical data.
import type { AddressSuggestion, CitySuggestion, Country, Currency } from "../types";

export const mockCountries: Country[] = [
	{
		code: "RU",
		name: "Россия",
		currency: "RUB",
		cities: [
			"Москва",
			"Санкт-Петербург",
			"Новосибирск",
			"Екатеринбург",
			"Казань",
			"Нижний Новгород",
			"Краснодар",
			"Сочи",
		],
		enabled: true,
	},
	{ code: "AM", name: "Армения", currency: "AMD", cities: ["Ереван", "Гюмри", "Ванадзор", "Вагаршапат", "Раздан"], enabled: true },
	{ code: "BY", name: "Беларусь", currency: "BYN", cities: ["Минск", "Гомель", "Витебск", "Брест", "Гродно"], enabled: true },
	{ code: "KZ", name: "Казахстан", currency: "KZT", cities: ["Алматы", "Астана", "Шымкент", "Караганда", "Актобе"], enabled: true },
	{ code: "GE", name: "Грузия", currency: "GEL", cities: ["Тбилиси", "Батуми", "Кутаиси", "Рустави"], enabled: true },
];

export const mockCurrencies: Currency[] = [
	{ code: "RUB", symbol: "₽", name: "Российский рубль", rate: 1, symbolBefore: false },
	{ code: "AMD", symbol: "֏", name: "Армянский драм", rate: 4.3, symbolBefore: false },
	{ code: "BYN", symbol: "Br", name: "Белорусский рубль", rate: 0.036, symbolBefore: false },
	{ code: "KZT", symbol: "₸", name: "Казахстанский тенге", rate: 5.6, symbolBefore: false },
	{ code: "GEL", symbol: "₾", name: "Грузинский лари", rate: 0.029, symbolBefore: false },
	{ code: "USD", symbol: "$", name: "Доллар США", rate: 0.011, symbolBefore: true },
	{ code: "EUR", symbol: "€", name: "Евро", rate: 0.0098, symbolBefore: true },
];

// Offline stand-in for DaData address suggestions. A small fixture filtered by
// substring — enough to exercise the checkout autocomplete + autofill without a
// network. The gateway returns the same shape from suggest/address.
const mockAddresses: AddressSuggestion[] = [
	{ value: "г Москва, ул Тверская, д 7", country: "Россия", city: "Москва", street: "ул Тверская, д 7", postalCode: "125009" },
	{ value: "г Москва, Ленинский пр-кт, д 32", country: "Россия", city: "Москва", street: "Ленинский пр-кт, д 32", postalCode: "119334" },
	{ value: "г Санкт-Петербург, Невский пр-кт, д 28", country: "Россия", city: "Санкт-Петербург", street: "Невский пр-кт, д 28", postalCode: "191186" },
	{ value: "г Казань, ул Баумана, д 44", country: "Россия", city: "Казань", street: "ул Баумана, д 44", postalCode: "420111" },
	{ value: "г Ереван, пр-кт Маштоца, д 12", country: "Армения", city: "Ереван", street: "пр-кт Маштоца, д 12", postalCode: "0009" },
];

export const suggestMockAddresses = (query: string, city?: string): AddressSuggestion[] => {
	const q = query.trim().toLowerCase();
	if (!q) return [];
	const inCity = city?.trim().toLowerCase();
	return mockAddresses
		.filter((a) => !inCity || a.city.toLowerCase() === inCity)
		.filter((a) => a.value.toLowerCase().includes(q) || a.city.toLowerCase().includes(q))
		.slice(0, 7);
};

// Offline stand-in for DaData city (locality) suggestions. The gateway returns
// the same shape from suggest/address bounded to city granularity.
const mockCities: CitySuggestion[] = [
	{ value: "г Москва", country: "Россия", city: "Москва", region: "Москва", postalCode: "" },
	{ value: "г Санкт-Петербург", country: "Россия", city: "Санкт-Петербург", region: "Санкт-Петербург", postalCode: "" },
	{ value: "г Казань", country: "Россия", city: "Казань", region: "Татарстан", postalCode: "" },
	{ value: "г Новосибирск", country: "Россия", city: "Новосибирск", region: "Новосибирская", postalCode: "" },
	{ value: "г Екатеринбург", country: "Россия", city: "Екатеринбург", region: "Свердловская", postalCode: "" },
	{ value: "г Ереван", country: "Армения", city: "Ереван", region: "Ереван", postalCode: "" },
];

export const suggestMockCities = (query: string): CitySuggestion[] => {
	const q = query.trim().toLowerCase();
	if (!q) return [];
	return mockCities.filter((c) => c.city.toLowerCase().includes(q)).slice(0, 7);
};
